import React from 'react';
import { Dayjs } from 'dayjs';
import { BaseActivityMessage } from '../BaseActivityMessage';
import { DatePicker } from '../DatePicker';

export interface DateActivityMessageProps {
  /**
   * Current date value (YYYY-MM-DD format string or Date)
   */
  value: string | Date | null;
  /**
   * Callback when date changes
   */
  onChange: (value: string | null) => void;
  /**
   * Callback when submit button is clicked
   */
  onSubmit: () => void;
  /**
   * Whether to show skip button
   */
  allowSkip?: boolean;
  /**
   * Skip button text
   */
  skipText?: string;
  /**
   * Submit button text
   */
  submitText?: string;
  /**
   * Callback when skip button is clicked
   */
  onSkip?: () => void;
  /**
   * Minimum allowed date (ISO string)
   */
  minDate?: string;
  /**
   * Maximum allowed date (ISO string)
   */
  maxDate?: string;
  /**
   * Whether the inputs are disabled
   */
  disabled?: boolean;
  /**
   * Function to determine if a specific date should be disabled
   * Receives a Dayjs object
   */
  shouldDisableDate?: (date: Dayjs) => boolean;
  /**
   * Locale for date picker (e.g., 'en', 'de')
   * Note: The DatePicker component handles localization internally
   */
  locale?: string;
  /**
   * Whether the submit button should be enabled
   */
  submitEnabled?: boolean;
  /**
   * Hide the submit button (used for locked / read-only rendering).
   */
  hideSubmit?: boolean;
  /**
   * Message content to display above the date picker
   */
  children?: React.ReactNode;
  /**
   * Sender type: 'bot' or 'user'
   */
  sender: 'bot' | 'user';
  /**
   * Avatar URL for the sender
   */
  avatarUrl?: string;
  /**
   * Timestamp for the message
   */
  timestamp?: string;
  /**
   * Custom bot display name
   */
  botName?: string;
}

/**
 * DateActivityMessage displays a date selection interface with submit and optional skip buttons.
 * This component is designed for interactive date selection in chat-like interfaces,
 * with support for localization, date constraints, and custom validation.
 */
export const DateActivityMessage: React.FC<DateActivityMessageProps> = ({
  value,
  onChange,
  onSubmit,
  allowSkip = false,
  skipText = 'Skip',
  submitText = 'Send',
  onSkip,
  minDate,
  maxDate,
  disabled = false,
  shouldDisableDate,
  submitEnabled = true,
  hideSubmit = false,
  children,
  sender,
  avatarUrl,
  timestamp,
  botName,
}) => {
  return (
    <BaseActivityMessage
      allowSkip={allowSkip}
      skipText={skipText}
      submitText={submitText}
      onSkip={onSkip}
      disabled={disabled}
      submitEnabled={submitEnabled}
      hideSubmit={hideSubmit}
      sender={sender}
      avatarUrl={avatarUrl}
      timestamp={timestamp}
      botName={botName}
      onSubmit={onSubmit}
      renderInput={() => (
        <DatePicker
          value={value && typeof value === 'string' ? value : ''}
          onChange={onChange}
          maxDate={maxDate ?? ''}
          minDate={minDate ?? ''}
          disabled={disabled || sender === 'user'}
          label={''}
          shouldDisableDate={shouldDisableDate}
        />
      )}
    >
      {children}
    </BaseActivityMessage>
  );
};
